import React, { useEffect, useState, useMemo } from "react";
import { NavLink, useParams } from "react-router-dom";
import axios from "axios";
import { updatePurchaseStatus } from "../api/requests";
import useWindowDimensions from "@helpers/hooks/windowDemensions";
import useDeliveryRange from "@helpers/hooks/useDeliveryRange";
import { useModal } from "@helpers/hooks/useModal";
import { orderAccessParams } from "@helpers/utils/orderAccess";
import HeartLoader from "@shared/HeartLoader";
import SuccessReveal from "./SuccessReveal";
import SuccessScene from "./SuccessScene";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PaymentSuccess = () => {
  const [orderDeteils, setOrderDeteils] = useState({});
  const [isLoading, setLoading] = useState(false);
  const modal = useModal();
  const { orderId } = useParams();
  const { height, width } = useWindowDimensions();
  // Wall orders: authoritative frame is wallProduct.frame (from prepare-order).
  // shippingAddress.frameType can be stale if a NOT_PAID order was reused.
  const orderFrameType = orderDeteils?.order?.wallProduct?.frame
    || orderDeteils?.order?.shippingAddress?.frameType;
  const { paymentSuccessDate } = useDeliveryRange(orderFrameType);

  const getOrder = async () => {
    try {
        // The order's uid/sessionId prove it's ours (the oid alone is guessable).
        const res = await axios.get(`${BASE_URL}/user/getorder/${orderId}`, { params: orderAccessParams() });
        return res.data;
    } catch (error) {
        console.error('Failed to fetch order:', error.message);
        return null;
    }
  }

  const loadData = async () => {
    const orderData = await getOrder();
    if (!orderData) {
    }
    setLoading(false);
    setOrderDeteils(orderData);

    // Update the purchase status in the AddToCart document
    if (orderData && orderData.order) {
      try {
        const sessionId = localStorage.getItem("sessionId") || "";

        await updatePurchaseStatus(
          sessionId,
          orderId,
          orderData.order.shippingAddress.finalAmount,
          "card" // Default to card, could be updated based on actual payment method if available
        );
        
      } catch (error) {
        console.error(`Error updating purchase status: ${error.message}`);
      }
    }
    
    // Add Flashy list operations for purchase completion
    // This handles Apple Pay, Bit, and any other payment methods that don't update Flashy in checkout
    if (window.flashy && orderData?.order?.shippingAddress) {
      const names = orderData.order.shippingAddress.fullName.trim().split(' ');
      const firstName = names[0];
      const lastName = names.length > 1 ? names.slice(1).join(' ') : '';

      // Track purchase event
      if (typeof window.flashy === 'function') {
        window.flashy('track', 'purchase', {
          order_id: orderId,
          value: orderData.order.shippingAddress.finalAmount,
          currency: 'ILS',
          items: [{
            name: 'Custom Photo Frame',
            quantity: orderData.imageCount,
            price: orderData.order.shippingAddress.finalAmount / orderData.imageCount,
            category: 'Frames'
          }]
        });
      }

      // Update lists - add to purchased, remove from abandoned
      if (typeof window.flashy.contacts.createOrUpdate === 'function') {
        window.flashy.contacts.createOrUpdate({
          "email": orderData.order.shippingAddress.email,
          "first_name": firstName,
          "last_name": lastName,
          "total_spent": orderData.order.shippingAddress.finalAmount,
          "frames_count": orderData.imageCount,
          "order_id": orderId,
          "lists": {
            28086: true,   // Add to "Purchased" list
            28236: false   // Remove from "Abandoned Cart" list
          }
        });
      }
    }

    // GA4 purchase event tracking
    if (window.gtag && orderData && orderData.order) {
      window.gtag('event', 'purchase', {
        transaction_id: orderId,
        value: orderData.order.shippingAddress.finalAmount,
        currency: 'ILS',
        items: [{
          item_name: 'Blends Frame',
          quantity: orderData.imageCount,
          price: orderData.order.shippingAddress.finalAmount / orderData.imageCount
        }],
        shipping: orderData.imageCount >= 5 ? 0 : 29.9
      });

    }
  };
  
  // Add this useEffect for pixel tracking (Facebook and TikTok)
  useEffect(() => {
    if (orderDeteils?.order?.shippingAddress?.finalAmount) {
      // Facebook Pixel tracking
      if (window.fbq) {
        window.fbq('track', 'Purchase', {
          value: orderDeteils.order.shippingAddress.finalAmount,
          currency: 'ILS'
        });
      }
      
      // TikTok Pixel tracking
      if (window.ttq) {
        window.ttq.track('CompletePayment', {
          content_id: orderId,
          content_type: 'product',
          content_name: 'Blends Frames',
          quantity: orderDeteils.imageCount,
          price: orderDeteils.order.shippingAddress.finalAmount,
          value: orderDeteils.order.shippingAddress.finalAmount,
          currency: 'ILS',
        });
      }
    }
  }, [orderDeteils, orderId]);
  
  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0); // start at the top so the curtain reveals the page cleanly

    // Clear navigation guard flags
    window.isNavigatingToSuccessPage = false;
    sessionStorage.removeItem('isNavigatingToSuccessPage');
    
    // Close all modals to ensure clean state
    modal("close", "checkout");
    modal("close", "mobileCheckout");
    modal("close", "applePayMobile");
    modal("close", "bitPayMobile");
    modal("close", "selectCardMobile");
    modal("close", "errorCart");
    
    // Load order data
    loadData();
    
    // Clear any localStorage data that might be leftover
    setTimeout(() => {
      if (localStorage.getItem("orderId")) {
        localStorage.removeItem("orderId");
      }
    }, 1000);
  }, []);

  const isMobile = useMemo(() => {
    return width < 768;
  })

  return (
    <>
      <SuccessReveal />

      {orderDeteils?.order && (
        <div>
          <SuccessScene
            isMobile={isMobile}
            orderId={orderId}
            frameType={orderFrameType}
            images={orderDeteils?.images}
            fullName={orderDeteils?.order?.shippingAddress?.fullName}
            address={orderDeteils?.order?.shippingAddress?.address || orderDeteils?.order?.shippingAddress?.line1 || orderDeteils?.order?.shippingAddress?.street}
            city={orderDeteils?.order?.shippingAddress?.city}
            phone={orderDeteils?.order?.shippingAddress?.phone || orderDeteils?.order?.shippingAddress?.phoneNumber}
            deliveryDate={paymentSuccessDate}
            imageCount={
              orderDeteils?.order?.wallProduct?.count
              || orderDeteils?.order?.imagesPayed
              || orderDeteils?.imageCount
              || (orderDeteils?.images?.length || 0)
            }
            creditsEarned={orderDeteils?.creditsEarned}
            freeShipping={(
              orderDeteils?.order?.wallProduct?.count
              || orderDeteils?.order?.imagesPayed
              || orderDeteils?.imageCount
              || 0
            ) >= 5}
          />
        </div>
      )}



      <section className="footer-btm xy">
      <p style={{lineHeight: "36px", margin: "0"}}>Copyright Blends 2021-{new Date().getFullYear()} &copy;</p>
        <div className="btm-wrap">
          <a
            href="#"
            title="leads to a floating screen 2"
            data-target="#float2"
            data-toggle="modal"
          >
            מדיניות פרטיות
          </a>
          <a
            href="#"
            title="leads to a floating screen 3"
            data-target="#float3"
            data-toggle="modal"
          >
            תנאי שימוש
          </a>
        </div>
      </section>
      <HeartLoader isLoading={isLoading} showText={false}/>
    </>
  );
};

export default PaymentSuccess;
